import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  BarChart3, 
  Clock, 
  AlertCircle, 
  Plane, 
  Download, 
  Calendar,
  Users,
  TrendingUp,
  Filter,
  Shield,
  MapPin,
  FileText,
  RefreshCw,
  CheckCircle,
  XCircle,
  Home,
  Wallet,
  History,
  Cake,
  Award
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import FlexBankAudit from './FlexBankAudit';
import { formatDateSafe } from '../utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reports = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuditTool, setShowAuditTool] = useState(false);
  const [auditUserId, setAuditUserId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [ptoTypeConfigs, setPtoTypeConfigs] = useState([]);
  const [wfhTypeConfigs, setWfhTypeConfigs] = useState([]);
  const [reportData, setReportData] = useState({
    shifts: [],
    onCall: [],
    pto: [],
    employeeSchedules: [],
    coverage: [],
    shiftAssignments: [],
    timeOffAvailability: [],
    scheduledVsActual: [],
    overtimeHours: [],
    swapChangeLog: [],
    complianceCheck: [],
    wfhFlexBank: [],
    ptoSummary: {},
    summary: {}
  });

  // Filter states
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    employeeId: 'all',
    reportType: 'employee_schedule' // Default to first report type
  });

  useEffect(() => {
    fetchEmployees();
    fetchReports();
    fetchTypeConfigs();
  }, []);

  const fetchTypeConfigs = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const headers = { Authorization: `Bearer ${token}` };
      const [ptoRes, wfhRes] = await Promise.all([
        axios.get(`${API}/pto-types`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/wfh-types`, { headers }).catch(() => ({ data: [] }))
      ]);
      setPtoTypeConfigs(ptoRes.data || []);
      setWfhTypeConfigs(wfhRes.data || []);
    } catch (error) {
      console.error('Failed to fetch type configs:', error);
    }
  };

  // Resolve PTO type value or ID to display name
  const resolvePTOType = (type) => {
    if (!type) return 'Unknown';
    const match = ptoTypeConfigs.find(t => t.value === type || t.id === type);
    if (match) return match.display_name || match.value;
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Resolve WFH type value or ID to display name
  const resolveWFHType = (type) => {
    if (!type) return 'WFH';
    const match = wfhTypeConfigs.find(t => t.value === type || t.id === type);
    if (match) return match.display_name || match.value;
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Auto-refresh when filters change (except on mount)
  useEffect(() => {
    const isInitialMount = filters.startDate && filters.endDate && filters.reportType;
    if (isInitialMount && employees.length > 0) {
      fetchReports();
    }
  }, [filters.startDate, filters.endDate, filters.employeeId, filters.reportType]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to view reports');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      // Fetch from unified users collection (active users only, exclude main admin account)
      const response = await axios.get(`${API}/users`, { headers });
      const activeUsers = (response.data || []).filter(u => u.is_active !== false && u.email !== 'admin@workmanager.com');
      setEmployees(activeUsers);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to load employees');
      }
    }
  };

  const fetchReports = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to view reports');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams({
        start_date: filters.startDate,
        end_date: filters.endDate,
        employee_id: filters.employeeId !== 'all' ? filters.employeeId : '',
        report_type: filters.reportType
      });

      const response = await axios.get(`${API}/reports?${params}`, { headers });
      setReportData(response.data);
      toast.success('Reports generated successfully');
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error(`Failed to load reports: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const toISO = (d) => {
    const tz = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tz).toISOString().split('T')[0];
  };

  const applyDatePreset = (preset) => {
    const now = new Date();
    let start, end;
    if (preset === 'today') {
      start = end = now;
    } else if (preset === 'week') {
      const day = now.getDay(); // 0 = Sunday
      start = new Date(now); start.setDate(now.getDate() - day);
      end = new Date(start); end.setDate(start.getDate() + 6);
    } else if (preset === 'month') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    } else if (preset === 'year') {
      start = new Date(now.getFullYear(), 0, 1);
      end = new Date(now.getFullYear(), 11, 31);
    } else {
      return;
    }
    setFilters(prev => ({ ...prev, startDate: toISO(start), endDate: toISO(end) }));
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString) => {
    return formatDateSafe(dateString, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      'tier1': 'Tier 1',
      'tier2': 'Tier 2', 
      'teamlead': 'Team Lead',
      'manager': 'Manager'
    };
    return roleNames[role] || 'Support';
  };

  const getCurrentReportConfig = () => {
    const reportConfigs = {
      'employee_schedule': {
        title: 'Employee Schedule',
        subtitle: 'Employees, Managers',
        icon: Calendar,
        color: 'blue-600',
        bgColor: 'blue-50',
        data: reportData.employeeSchedules || [],
        emptyMessage: 'No employee schedules found',
        renderItem: (schedule, index) => (
          <div key={index} className="p-3 bg-blue-50 rounded-lg">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-medium text-gray-900">{schedule.employee_name}</p>
                <p className="text-sm text-gray-600">{formatDate(schedule.date)} • {getRoleDisplayName(schedule.role)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium">{formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}</p>
                <p className="text-xs text-gray-500">{schedule.location || 'Main Office'}</p>
              </div>
            </div>
          </div>
        )
      },
      'celebrations': {
        title: 'Birthdays & Anniversaries',
        subtitle: 'Managers, HR',
        icon: Cake,
        color: 'pink-600',
        bgColor: 'pink-50',
        data: [
          ...(reportData.celebrations?.birthdays || []).map(b => ({ ...b, _type: 'birthday' })),
          ...(reportData.celebrations?.anniversaries || []).map(a => ({ ...a, _type: 'anniversary' })),
        ].sort((x, y) => x.date.localeCompare(y.date)),
        emptyMessage: 'No birthdays or anniversaries in this range',
        renderItem: (item, index) => (
          item._type === 'birthday' ? (
            <div key={`b-${item.user_id}-${item.date}-${index}`} className="p-3 bg-pink-50 rounded-lg" data-testid={`celebration-birthday-${index}`}>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-pink-100 rounded-full"><Cake className="h-4 w-4 text-pink-600" /></div>
                <div>
                  <p className="font-medium text-gray-900">{item.name}</p>
                  {item.job_title && <p className="text-xs text-gray-500">{item.job_title}</p>}
                  <p className="text-sm text-pink-700 mt-0.5">Birthday • {formatDate(item.date)}</p>
                </div>
              </div>
            </div>
          ) : (
            <div key={`a-${item.user_id}-${item.date}-${index}`} className="p-3 bg-amber-50 rounded-lg" data-testid={`celebration-anniversary-${index}`}>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-100 rounded-full"><Award className="h-4 w-4 text-amber-600" /></div>
                <div>
                  <p className="font-medium text-gray-900">{item.name}</p>
                  {item.job_title && <p className="text-xs text-gray-500">{item.job_title}</p>}
                  <p className="text-sm text-amber-700 mt-0.5">{item.years} {item.years === 1 ? 'year' : 'years'} of service • {formatDate(item.date)}</p>
                </div>
              </div>
            </div>
          )
        )
      },
      'coverage': {
        title: 'Coverage Report',
        subtitle: 'Managers, Supervisors',
        icon: Shield,
        color: 'green-600',
        bgColor: 'green-50',
        data: reportData.coverage || [],
        emptyMessage: 'No coverage data available',
        renderItem: (coverage, index) => (
          <div key={index} className="p-3 bg-green-50 rounded-lg">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium text-gray-900">{coverage.time_block}</p>
                <p className="text-sm text-gray-600">{coverage.role}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium">{coverage.assigned}/{coverage.required}</p>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  coverage.coverage_status === 'Adequate' ? 'bg-green-100 text-green-800' : 
                  coverage.coverage_status === 'Understaffed' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {coverage.coverage_status}
                </span>
              </div>
            </div>
          </div>
        )
      },
      'shift_assignment': {
        title: 'Shift Assignment',
        subtitle: 'Managers, Team Leads',
        icon: MapPin,
        color: 'purple-600',
        bgColor: 'purple-50',
        data: reportData.shiftAssignments || [],
        emptyMessage: 'No shift assignments found',
        renderItem: (assignment, index) => (
          <div key={index} className="p-3 bg-purple-50 rounded-lg">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-medium text-gray-900">{assignment.employee_name}</p>
                <p className="text-sm text-gray-600">{assignment.department} • {assignment.location}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium">{getRoleDisplayName(assignment.role)}</p>
                <p className="text-xs text-gray-500">{assignment.shift_count} shifts</p>
              </div>
            </div>
          </div>
        )
      },
      'time_off_availability': {
        title: 'Time-Off & Availability',
        subtitle: 'Managers, HR',
        icon: Plane,
        color: 'indigo-600',
        bgColor: 'indigo-50',
        data: reportData.timeOffAvailability || [],
        emptyMessage: 'No availability data found',
        renderItem: (availability, index) => (
          <div key={index} className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
            <div className="flex justify-between items-start mb-3">
              <div>
                <p className="font-medium text-gray-900">{availability.employee_name}</p>
                <p className="text-sm text-gray-600">Total PTO: {availability.pto_days} days</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium">{availability.availability}</p>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  availability.status === 'Available' ? 'bg-green-100 text-green-800' : 
                  availability.status === 'Limited' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {availability.status}
                </span>
              </div>
            </div>
            
            {/* PTO Type Breakdown */}
            {availability.pto_breakdown && Object.keys(availability.pto_breakdown).length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-medium text-gray-700 mb-2">PTO Breakdown:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(availability.pto_breakdown).map(([type, count]) => (
                    <span key={type} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-indigo-100 text-indigo-800">
                      {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}: {count}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Detailed PTO Requests */}
            {availability.pto_requests && availability.pto_requests.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-700">Recent Requests:</p>
                {availability.pto_requests.slice().sort((a, b) => (b.start_date || '').localeCompare(a.start_date || '')).slice(0, 3).map((request, idx) => (
                  <div key={request.id || idx} className="p-2 bg-white rounded border border-indigo-100">
                    <div className="flex justify-between items-start text-xs">
                      <div>
                        <p className="font-medium text-gray-900">
                          {formatDateSafe(request.start_date)} - {formatDateSafe(request.end_date)}
                        </p>
                        <p className="text-indigo-600 capitalize">
                          {resolvePTOType(request.pto_type)}
                        </p>
                        {request.reason && (
                          <p className="text-gray-600 mt-1 italic">"{request.reason}"</p>
                        )}
                      </div>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                        request.status === 'approved' ? 'bg-green-100 text-green-700' :
                        request.status === 'denied' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {request.status}
                      </span>
                    </div>
                  </div>
                ))}
                {availability.pto_requests.length > 3 && (
                  <p className="text-xs text-gray-500">
                    +{availability.pto_requests.length - 3} more requests
                  </p>
                )}
              </div>
            )}
          </div>
        )
      },
      'scheduled_vs_actual': {
        title: 'Scheduled vs. Actual Hours',
        subtitle: 'Managers, Payroll',
        icon: BarChart3,
        color: 'blue-600',
        bgColor: 'blue-50',
        data: reportData.scheduledVsActual || [],
        emptyMessage: 'No hours comparison data available',
        renderItem: (hours, index) => (
          <div key={index} className="p-3 bg-blue-50 rounded-lg">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium text-gray-900">{hours.employee_name}</p>
                <p className="text-sm text-gray-600">Variance: {hours.variance}h</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium">S: {hours.scheduled_hours}h | A: {hours.actual_hours}h</p>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  Math.abs(hours.variance) <= 1 ? 'bg-green-100 text-green-800' : 
                  Math.abs(hours.variance) <= 3 ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {Math.abs(hours.variance) <= 1 ? 'On Track' : Math.abs(hours.variance) <= 3 ? 'Minor Variance' : 'Major Variance'}
                </span>
              </div>
            </div>
          </div>
        )
      },
      'overtime_hours': {
        title: 'Overtime / Hours Worked',
        subtitle: 'Managers, Finance',
        icon: TrendingUp,
        color: 'orange-600',
        bgColor: 'orange-50',
        data: reportData.overtimeHours || [],
        emptyMessage: 'No overtime data available',
        renderItem: (overtime, index) => (
          <div key={index} className="p-3 bg-orange-50 rounded-lg">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium text-gray-900">{overtime.employee_name}</p>
                <p className="text-sm text-gray-600">Total: {overtime.total_hours}h</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium">R: {overtime.regular_hours}h | OT: {overtime.overtime_hours}h</p>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  overtime.overtime_hours === 0 ? 'bg-green-100 text-green-800' : 
                  overtime.overtime_hours <= 5 ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {overtime.overtime_hours === 0 ? 'No OT' : overtime.overtime_hours <= 5 ? 'Moderate OT' : 'High OT'}
                </span>
              </div>
            </div>
          </div>
        )
      },
      'swap_change_log': {
        title: 'Swap & Change Log',
        subtitle: 'Managers, HR',
        icon: RefreshCw,
        color: 'cyan-600',
        bgColor: 'cyan-50',
        data: reportData.swapChangeLog || [],
        emptyMessage: 'No change log entries found',
        renderItem: (change, index) => (
          <div key={index} className="p-3 bg-cyan-50 rounded-lg">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-medium text-gray-900">{change.employee_name}</p>
                <p className="text-sm text-gray-600">{change.change_type} • {formatDate(change.date)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium">{change.details}</p>
                <p className="text-xs text-gray-500">By: {change.manager}</p>
              </div>
            </div>
          </div>
        )
      },
      'compliance_check': {
        title: 'Compliance / Rule Check',
        subtitle: 'Managers, HR/Legal',
        icon: CheckCircle,
        color: 'green-600',
        bgColor: 'green-50',
        data: reportData.complianceCheck || [],
        emptyMessage: 'No compliance issues found',
        renderItem: (compliance, index) => (
          <div key={index} className="p-3 bg-green-50 rounded-lg">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium text-gray-900">{compliance.employee_name}</p>
                <p className="text-sm text-gray-600">{compliance.rule}</p>
              </div>
              <div className="text-right">
                <div className="flex items-center space-x-2">
                  {compliance.status === 'Compliant' ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-600" />
                  )}
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    compliance.status === 'Compliant' ? 'bg-green-100 text-green-800' : 
                    'bg-red-100 text-red-800'
                  }`}>
                    {compliance.status}
                  </span>
                </div>
                {compliance.action_required && (
                  <p className="text-xs text-gray-500 mt-1">{compliance.action_required}</p>
                )}
              </div>
            </div>
          </div>
        )
      },
      'wfh_flex_bank': {
        title: 'WFH / Flex Bank Report',
        subtitle: 'Managers, HR',
        icon: Home,
        color: 'teal-600',
        bgColor: 'teal-50',
        data: reportData.wfhFlexBank || [],
        emptyMessage: 'No WFH/Flex Bank data found',
        renderItem: (employee, index) => (
          <div key={index} className="p-4 bg-teal-50 rounded-lg border border-teal-200">
            <div className="flex justify-between items-start mb-3">
              <div>
                <p className="font-medium text-gray-900">{employee.employee_name}</p>
                <p className="text-sm text-gray-600">
                  Flex Bank: <span className="font-semibold">{employee.flex_bank_balance}</span> / {employee.flex_bank_default} days
                </p>
              </div>
              <div className="text-right flex flex-col items-end space-y-1">
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  employee.utilization_pct >= 80 ? 'bg-red-100 text-red-800' :
                  employee.utilization_pct >= 50 ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {employee.utilization_pct}% Flex Bank Used
                </div>
                {(user?.role === 'admin' || user?.role === 'manager') && (
                  <button
                    onClick={() => {
                      setAuditUserId(employee.employee_id);
                      setShowAuditTool(true);
                    }}
                    className="inline-flex items-center px-2 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                    data-testid={`audit-btn-${employee.employee_id}`}
                  >
                    <History className="h-3 w-3 mr-1" />
                    Audit
                  </button>
                )}
              </div>
            </div>
            
            {/* Flex Bank Stats Row - Only explicit flex_bank type */}
            <div className="mb-3">
              <p className="text-xs font-medium text-blue-700 mb-1">Flex Bank (Counts Against Balance)</p>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="bg-blue-50 rounded p-2 border border-blue-200">
                  <p className="text-lg font-bold text-blue-600">{employee.flex_bank_days_used || 0}</p>
                  <p className="text-xs text-gray-500">Days Used</p>
                </div>
                <div className="bg-blue-50 rounded p-2 border border-blue-200">
                  <p className="text-lg font-bold text-blue-600">{employee.remaining_days ?? employee.flex_bank_balance}</p>
                  <p className="text-xs text-gray-500">Balance</p>
                </div>
                <div className="bg-blue-50 rounded p-2 border border-blue-200">
                  <p className="text-lg font-bold text-yellow-600">{employee.flex_bank_pending || 0}</p>
                  <p className="text-xs text-gray-500">Pending</p>
                </div>
                <div className="bg-blue-50 rounded p-2 border border-blue-200">
                  <p className="text-lg font-bold text-green-600">{employee.flex_bank_approved || 0}</p>
                  <p className="text-xs text-gray-500">Approved</p>
                </div>
              </div>
            </div>
            
            {/* Legacy WFH Stats Row - Old requests without type */}
            {(employee.legacy_wfh_days_used > 0 || employee.legacy_wfh_pending > 0 || employee.legacy_wfh_approved > 0) && (
              <div className="mb-3">
                <p className="text-xs font-medium text-gray-700 mb-1">WFH (Legacy - No Balance Deduction)</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-gray-50 rounded p-2 border border-gray-200">
                    <p className="text-lg font-bold text-gray-600">{employee.legacy_wfh_days_used || 0}</p>
                    <p className="text-xs text-gray-500">Days</p>
                  </div>
                  <div className="bg-gray-50 rounded p-2 border border-gray-200">
                    <p className="text-lg font-bold text-yellow-600">{employee.legacy_wfh_pending || 0}</p>
                    <p className="text-xs text-gray-500">Pending</p>
                  </div>
                  <div className="bg-gray-50 rounded p-2 border border-gray-200">
                    <p className="text-lg font-bold text-green-600">{employee.legacy_wfh_approved || 0}</p>
                    <p className="text-xs text-gray-500">Approved</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* On-Call WFH Stats Row */}
            {(employee.on_call_days_used > 0 || employee.on_call_pending > 0 || employee.on_call_approved > 0) && (
              <div className="mb-3">
                <p className="text-xs font-medium text-orange-700 mb-1">On-Call WFH (No Balance Deduction)</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-orange-50 rounded p-2 border border-orange-200">
                    <p className="text-lg font-bold text-orange-600">{employee.on_call_days_used || 0}</p>
                    <p className="text-xs text-gray-500">Days</p>
                  </div>
                  <div className="bg-orange-50 rounded p-2 border border-orange-200">
                    <p className="text-lg font-bold text-yellow-600">{employee.on_call_pending || 0}</p>
                    <p className="text-xs text-gray-500">Pending</p>
                  </div>
                  <div className="bg-orange-50 rounded p-2 border border-orange-200">
                    <p className="text-lg font-bold text-green-600">{employee.on_call_approved || 0}</p>
                    <p className="text-xs text-gray-500">Approved</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Business Directed Stats Row */}
            {(employee.business_directed_days_used > 0 || employee.business_directed_approved > 0) && (
              <div className="mb-3">
                <p className="text-xs font-medium text-purple-700 mb-1">Business Directed (No Balance Deduction)</p>
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div className="bg-purple-50 rounded p-2 border border-purple-200">
                    <p className="text-lg font-bold text-purple-600">{employee.business_directed_days_used || 0}</p>
                    <p className="text-xs text-gray-500">Days</p>
                  </div>
                  <div className="bg-purple-50 rounded p-2 border border-purple-200">
                    <p className="text-lg font-bold text-green-600">{employee.business_directed_approved || 0}</p>
                    <p className="text-xs text-gray-500">Approved</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Total WFH Summary */}
            {employee.total_wfh_days > 0 && (
              <div className="mb-3 bg-gray-100 rounded p-2 text-center">
                <p className="text-xs text-gray-500">Total WFH Days (All Types)</p>
                <p className="text-xl font-bold text-gray-700">{employee.total_wfh_days}</p>
              </div>
            )}
            
            {/* Recent Requests */}
            {employee.requests && employee.requests.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-700">Recent WFH Requests:</p>
                {employee.requests.slice().sort((a, b) => (b.start_date || '').localeCompare(a.start_date || '')).slice(0, 3).map((request, idx) => {
                  const typeLabel = resolveWFHType(request.wfh_type);
                  const borderClass = request.wfh_type === 'on_call' ? 'border-orange-200' : 
                                     request.wfh_type === 'business_directed' ? 'border-purple-200' : 
                                     request.wfh_type === 'flex_bank' ? 'border-blue-200' : 'border-gray-200';
                  const textClass = request.wfh_type === 'on_call' ? 'text-orange-600' : 
                                   request.wfh_type === 'business_directed' ? 'text-purple-600' : 
                                   request.wfh_type === 'flex_bank' ? 'text-blue-600' : 'text-gray-600';
                  const badgeClass = request.wfh_type === 'on_call' ? 'bg-orange-100 text-orange-700' : 
                                    request.wfh_type === 'business_directed' ? 'bg-purple-100 text-purple-700' : 
                                    request.wfh_type === 'flex_bank' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700';
                  return (
                    <div key={request.id || idx} className={`p-2 bg-white rounded border ${borderClass}`}>
                      <div className="flex justify-between items-start text-xs">
                        <div>
                          <p className="font-medium text-gray-900">
                            {formatDateSafe(request.start_date)} - {formatDateSafe(request.end_date)}
                          </p>
                          <div className="flex items-center space-x-2">
                            <p className={textClass}>{request.days} day(s)</p>
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${badgeClass}`}>
                              {typeLabel}
                            </span>
                          </div>
                          {request.reason && (
                            <p className="text-gray-600 mt-1 italic">"{request.reason}"</p>
                          )}
                        </div>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                          request.status === 'approved' ? 'bg-green-100 text-green-700' :
                          request.status === 'denied' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {request.status}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {employee.requests.length > 3 && (
                  <p className="text-xs text-gray-500">
                    +{employee.requests.length - 3} more requests
                  </p>
                )}
              </div>
            )}
          </div>
        )
      },
      'pto_summary': {
        title: 'PTO Summary Report',
        subtitle: 'Managers, HR',
        icon: Plane,
        color: 'rose-600',
        bgColor: 'rose-50',
        data: reportData.ptoSummary?.by_employee || [],
        emptyMessage: 'No PTO data found',
        customHeader: () => {
          const summary = reportData.ptoSummary || {};
          return (
            <div className="mb-4 p-4 bg-rose-100 rounded-lg">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-rose-700">{summary.total_requests || 0}</p>
                  <p className="text-xs text-gray-600">Total Requests</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-rose-700">{summary.total_days || 0}</p>
                  <p className="text-xs text-gray-600">Total Days</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{summary.by_status?.approved || 0}</p>
                  <p className="text-xs text-gray-600">Approved</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-600">{summary.by_status?.pending || 0}</p>
                  <p className="text-xs text-gray-600">Pending</p>
                </div>
              </div>
              
              {/* PTO by Type breakdown */}
              {summary.by_type && summary.by_type.length > 0 && (
                <div className="mt-4 pt-4 border-t border-rose-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">By Type:</p>
                  <div className="flex flex-wrap gap-2">
                    {summary.by_type.map((item) => (
                      <span key={item.type} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-white text-rose-800 border border-rose-200">
                        {item.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: {item.days} days ({item.count} req)
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        },
        renderItem: (employee, index) => (
          <div key={index} className="p-4 bg-rose-50 rounded-lg border border-rose-200">
            <div className="flex justify-between items-start mb-3">
              <div>
                <p className="font-medium text-gray-900">{employee.employee_name}</p>
                <p className="text-sm text-gray-600">
                  Total: <span className="font-semibold">{employee.total_days}</span> days ({employee.total_requests} requests)
                </p>
              </div>
              <div className="text-right">
                <div className="flex space-x-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {employee.approved_days} approved
                  </span>
                  {employee.pending_days > 0 && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      {employee.pending_days} pending
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Type Breakdown */}
            {employee.breakdown_by_type && Object.keys(employee.breakdown_by_type).length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-medium text-gray-700 mb-2">Breakdown:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(employee.breakdown_by_type).map(([type, days]) => (
                    <span key={type} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-rose-100 text-rose-800">
                      {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: {days} days
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Recent Requests */}
            {employee.requests && employee.requests.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-700">Recent Requests:</p>
                {employee.requests.slice().sort((a, b) => (b.start_date || '').localeCompare(a.start_date || '')).slice(0, 3).map((request, idx) => (
                  <div key={request.id || idx} className="p-2 bg-white rounded border border-rose-100">
                    <div className="flex justify-between items-start text-xs">
                      <div>
                        <p className="font-medium text-gray-900">
                          {formatDateSafe(request.start_date)} - {formatDateSafe(request.end_date)}
                        </p>
                        <p className="text-rose-600 capitalize">
                          {resolvePTOType(request.pto_type)} • {request.days} day(s)
                          {request.exclude_weekends && <span className="text-gray-500"> (weekdays only)</span>}
                          {request.is_partial_day && <span className="text-gray-500"> (partial)</span>}
                        </p>
                        {request.reason && (
                          <p className="text-gray-600 mt-1 italic">"{request.reason}"</p>
                        )}
                      </div>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                        request.status === 'approved' ? 'bg-green-100 text-green-700' :
                        request.status === 'denied' ? 'bg-red-100 text-red-700' :
                        request.status === 'cancelled' ? 'bg-gray-100 text-gray-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {request.status}
                      </span>
                    </div>
                  </div>
                ))}
                {employee.requests.length > 3 && (
                  <p className="text-xs text-gray-500">
                    +{employee.requests.length - 3} more requests
                  </p>
                )}
              </div>
            )}
          </div>
        )
      }
    };
    
    return reportConfigs[filters.reportType] || reportConfigs['employee_schedule'];
  };

  const exportReports = () => {
    console.log('Export button clicked, current report type:', filters.reportType);
    console.log('Current report data:', reportData);
    
    let csvContent = "data:text/csv;charset=utf-8,";
    
    // Get the current report configuration and data
    const config = getCurrentReportConfig();
    const currentData = config.data;
    
    if (!currentData || currentData.length === 0) {
      toast.error('No data available to export');
      return;
    }
    
    // Helper function to format UTC timestamps for CSV export
    const formatUTCForCSV = (timestamp) => {
      if (!timestamp) return '';
      let utcTimestamp = timestamp;
      if (typeof timestamp === 'string' && !timestamp.endsWith('Z') && !timestamp.includes('+')) {
        utcTimestamp = timestamp + 'Z';
      }
      return new Date(utcTimestamp).toLocaleString();
    };
    
    // Add title and headers based on report type
    csvContent += `${config.title.toUpperCase()}\n`;
    csvContent += `Generated: ${new Date().toLocaleString()}\n`;
    csvContent += `Date Range: ${filters.startDate} to ${filters.endDate}\n`;
    csvContent += `Employee Filter: ${filters.employeeId === 'all' ? 'All Employees' : employees.find(e => e.id === filters.employeeId)?.name || 'Unknown'}\n\n`;
    
    // Add data based on report type
    switch(filters.reportType) {
      case 'employee_schedule':
        csvContent += 'Employee,Date,Start Time,End Time,Role,Location\n';
        currentData.forEach(item => {
          csvContent += `"${item.employee_name}","${formatDate(item.date)}","${formatTime(item.start_time)}","${formatTime(item.end_time)}","${getRoleDisplayName(item.role)}","${item.location || 'Main Office'}"\n`;
        });
        break;
        
      case 'celebrations': {
        const bdays = reportData.celebrations?.birthdays || [];
        const annivs = reportData.celebrations?.anniversaries || [];
        csvContent += 'BIRTHDAYS\n';
        csvContent += 'Name,Job Title,Date\n';
        bdays.forEach(item => {
          csvContent += `"${item.name}","${item.job_title || ''}","${formatDate(item.date)}"\n`;
        });
        csvContent += '\nWORK ANNIVERSARIES\n';
        csvContent += 'Name,Job Title,Date,Years of Service\n';
        annivs.forEach(item => {
          csvContent += `"${item.name}","${item.job_title || ''}","${formatDate(item.date)}",${item.years}\n`;
        });
        break;
      }
        
      case 'coverage':
        csvContent += 'Time Block,Role,Required,Assigned,Coverage Status\n';
        currentData.forEach(item => {
          csvContent += `"${item.time_block}","${item.role}",${item.required},${item.assigned},"${item.coverage_status}"\n`;
        });
        break;
        
      case 'shift_assignment':
        csvContent += 'Employee,Role,Department,Location,Shift Count\n';
        currentData.forEach(item => {
          csvContent += `"${item.employee_name}","${getRoleDisplayName(item.role)}","${item.department}","${item.location}",${item.shift_count}\n`;
        });
        break;
        
      case 'time_off_availability':
        csvContent += 'Employee,Total PTO Days,PTO Breakdown,Availability,Status,Request Details\n';
        currentData.forEach(item => {
          // Format PTO breakdown
          let ptoBreakdown = '';
          if (item.pto_breakdown && Object.keys(item.pto_breakdown).length > 0) {
            ptoBreakdown = Object.entries(item.pto_breakdown)
              .map(([type, count]) => `${type.replace('_', ' ')}: ${count}`)
              .join('; ');
          } else {
            ptoBreakdown = 'No PTO taken';
          }
          
          // Format request details
          let requestDetails = '';
          if (item.pto_requests && item.pto_requests.length > 0) {
            requestDetails = item.pto_requests
              .map(req => `${formatDateSafe(req.start_date)}-${formatDateSafe(req.end_date)} (${resolvePTOType(req.pto_type)}, ${req.status}${req.reason ? ': ' + req.reason : ''})`)
              .join(' | ');
          } else {
            requestDetails = 'No recent requests';
          }
          
          csvContent += `"${item.employee_name}",${item.pto_days},"${ptoBreakdown}","${item.availability}","${item.status}","${requestDetails}"\n`;
        });
        break;
        
      case 'scheduled_vs_actual':
        csvContent += 'Employee,Scheduled Hours,Actual Hours,Variance\n';
        currentData.forEach(item => {
          csvContent += `"${item.employee_name}",${item.scheduled_hours},${item.actual_hours},${item.variance}\n`;
        });
        break;
        
      case 'overtime_hours':
        csvContent += 'Employee,Regular Hours,Overtime Hours,Total Hours\n';
        currentData.forEach(item => {
          csvContent += `"${item.employee_name}",${item.regular_hours},${item.overtime_hours},${item.total_hours}\n`;
        });
        break;
        
      case 'swap_change_log':
        csvContent += 'Employee,Date,Change Type,Details,Manager\n';
        currentData.forEach(item => {
          csvContent += `"${item.employee_name}","${formatDate(item.date)}","${item.change_type}","${item.details}","${item.manager}"\n`;
        });
        break;
        
      case 'compliance_check':
        csvContent += 'Employee,Rule,Status,Details,Action Required\n';
        currentData.forEach(item => {
          csvContent += `"${item.employee_name}","${item.rule}","${item.status}","${item.details}","${item.action_required || 'None'}"\n`;
        });
        break;
        
      case 'wfh_flex_bank':
        // Summary section with all WFH types
        csvContent += 'WFH / FLEX BANK SUMMARY\n';
        csvContent += 'Employee,Flex Bank Balance,Default Days,Flex Bank Used,Flex Bank Pending,Flex Bank Approved,Utilization %,Legacy WFH Days,Legacy Pending,Legacy Approved,On-Call Days,On-Call Pending,On-Call Approved,Business Directed Days,Business Directed Approved,Total WFH Days (All Types)\n';
        currentData.forEach(item => {
          csvContent += `"${item.employee_name}",${item.flex_bank_balance || item.remaining_days || 0},${item.flex_bank_default},${item.flex_bank_days_used || 0},${item.flex_bank_pending || 0},${item.flex_bank_approved || 0},${item.utilization_pct || 0}%,${item.legacy_wfh_days_used || 0},${item.legacy_wfh_pending || 0},${item.legacy_wfh_approved || 0},${item.on_call_days_used || 0},${item.on_call_pending || 0},${item.on_call_approved || 0},${item.business_directed_days_used || 0},${item.business_directed_approved || 0},${item.total_wfh_days || 0}\n`;
        });
        
        // Detailed requests section with WFH Type
        csvContent += '\n\nDETAILED WFH REQUESTS\n';
        csvContent += 'Employee,Request ID,WFH Type,Start Date,End Date,Days,Hours,Status,Reason,Submitted Date,Action By,Action Date,Notes,Historical\n';
        currentData.forEach(item => {
          if (item.requests && item.requests.length > 0) {
            item.requests.forEach(req => {
              const submittedDate = formatUTCForCSV(req.created_at);
              const actionBy = req.action_by || '';
              const actionDate = formatUTCForCSV(req.action_at);
              // Get WFH type display name
              const wfhTypeDisplay = resolveWFHType(req.wfh_type);
              csvContent += `"${item.employee_name}","${req.id}","${wfhTypeDisplay}","${req.start_date}","${req.end_date}",${req.days},${req.hours || 8},"${req.status}","${(req.reason || '').replace(/"/g, '""')}","${submittedDate}","${actionBy}","${actionDate}","${(req.notes || '').replace(/"/g, '""')}",${req.is_historical ? 'Yes' : 'No'}\n`;
            });
          }
        });
        break;
        
      case 'pto_summary':
        // Summary section
        csvContent += 'PTO SUMMARY\n';
        csvContent += 'Employee,Total Days,Total Requests,Approved Days,Pending Days,PTO Breakdown\n';
        currentData.forEach(item => {
          const breakdown = Object.entries(item.breakdown_by_type || {})
            .map(([type, days]) => `${type}: ${days}`)
            .join('; ');
          csvContent += `"${item.employee_name}",${item.total_days},${item.total_requests},${item.approved_days},${item.pending_days},"${breakdown}"\n`;
        });
        
        // Detailed requests section
        csvContent += '\n\nDETAILED PTO REQUESTS\n';
        csvContent += 'Employee,Request ID,PTO Type,Start Date,End Date,Days,Status,Partial Day,Start Time,End Time,Exclude Weekends,Reason,Submitted Date,Action By,Action Date,Notes\n';
        currentData.forEach(item => {
          if (item.requests && item.requests.length > 0) {
            item.requests.forEach(req => {
              const submittedDate = formatUTCForCSV(req.created_at);
              const actionBy = req.action_by || '';
              const actionDate = formatUTCForCSV(req.action_at);
              csvContent += `"${item.employee_name}","${req.id}","${resolvePTOType(req.pto_type)}","${req.start_date}","${req.end_date}",${req.days},"${req.status}",${req.is_partial_day ? 'Yes' : 'No'},"${req.start_time || ''}","${req.end_time || ''}",${req.exclude_weekends ? 'Yes' : 'No'},"${(req.reason || '').replace(/"/g, '""')}","${submittedDate}","${actionBy}","${actionDate}","${(req.notes || '').replace(/"/g, '""')}"\n`;
            });
          }
        });
        break;
        
      default:
        toast.error('Unknown report type');
        return;
    }
    
    // Create and trigger download
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `shiftela_${filters.reportType}_${filters.startDate}_${filters.endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`${config.title} exported successfully`);
  };

  return (
    <div className="space-y-6">
      {/* Flex Bank Audit Tool Modal */}
      <FlexBankAudit 
        isOpen={showAuditTool} 
        onClose={() => {
          setShowAuditTool(false);
          setAuditUserId(null);
        }}
        initialUserId={auditUserId}
      />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-600 mt-1">
            Analyze workforce data and generate insights
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {(user?.role === 'admin' || user?.role === 'manager') && (
            <Button 
              variant="outline" 
              onClick={() => setShowAuditTool(true)} 
              className="flex items-center space-x-2"
              data-testid="open-audit-tool-btn"
            >
              <History className="h-4 w-4" />
              <span>Flex Bank Audit</span>
            </Button>
          )}
          <Button onClick={exportReports} className="flex items-center space-x-2">
            <Download className="h-4 w-4" />
            <span>Export CSV</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="shadow-lg border-0">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-blue-600" />
            <span>Report Filters</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="employee">Employee</Label>
              <Select value={filters.employeeId} onValueChange={(value) => handleFilterChange('employeeId', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {employees.map((employee) => (
                    <SelectItem key={employee.id} value={employee.id}>
                      {employee.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="report-type">Report Type</Label>
              <Select value={filters.reportType} onValueChange={(value) => handleFilterChange('reportType', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select report type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee_schedule">Employee Schedule</SelectItem>
                  <SelectItem value="celebrations">Birthdays & Anniversaries</SelectItem>
                  <SelectItem value="coverage">Coverage Report</SelectItem>
                  <SelectItem value="shift_assignment">Shift Assignment</SelectItem>
                  <SelectItem value="time_off_availability">Time-Off & Availability</SelectItem>
                  <SelectItem value="pto_summary">PTO Summary Report</SelectItem>
                  <SelectItem value="wfh_flex_bank">WFH / Flex Bank Report</SelectItem>
                  <SelectItem value="scheduled_vs_actual">Scheduled vs. Actual Hours</SelectItem>
                  <SelectItem value="overtime_hours">Overtime / Hours Worked</SelectItem>
                  <SelectItem value="swap_change_log">Swap & Change Log</SelectItem>
                  <SelectItem value="compliance_check">Compliance / Rule Check</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500 mr-1">Quick range:</span>
            {[
              { key: 'today', label: 'Today' },
              { key: 'week', label: 'This Week' },
              { key: 'month', label: 'This Month' },
              { key: 'year', label: 'This Year' },
            ].map(p => (
              <Button
                key={p.key}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => applyDatePreset(p.key)}
                data-testid={`date-preset-${p.key}`}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <div className="mt-4">
            <Button onClick={fetchReports} disabled={loading} className="flex items-center space-x-2">
              <BarChart3 className="h-4 w-4" />
              <span>{loading ? 'Generating Reports...' : 'Generate Reports'}</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="shadow-lg border-0 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total Shifts</p>
                <p className="text-3xl font-bold">{reportData.summary?.total_shifts || 0}</p>
              </div>
              <div className="p-3 bg-white bg-opacity-20 rounded-full">
                <Clock className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0 bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">Coverage Issues</p>
                <p className="text-3xl font-bold">{reportData.summary?.coverage_gaps || 0}</p>
              </div>
              <div className="p-3 bg-white bg-opacity-20 rounded-full">
                <AlertCircle className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0 bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Overtime Hours</p>
                <p className="text-3xl font-bold">{reportData.summary?.overtime_hours || 0}</p>
              </div>
              <div className="p-3 bg-white bg-opacity-20 rounded-full">
                <TrendingUp className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Single Report Display */}
      <div className="w-full">
        {(() => {
          const config = getCurrentReportConfig();
          const Icon = config.icon;
          
          return (
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Icon className={`h-5 w-5 text-${config.color}`} />
                  <span>{config.title}</span>
                  <span className="text-sm text-gray-500 font-normal">• {config.subtitle}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Custom header for reports with summary data */}
                {config.customHeader && config.customHeader()}
                
                {config.data.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[600px] overflow-y-auto">
                    {config.data.map(config.renderItem)}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Icon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>{config.emptyMessage}</p>
                    <p className="text-sm">Adjust filters or date range to see data</p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}
      </div>
    </div>
  );
};

export default Reports;